/**
 * Custom / self-built agent journey — protocol `custom` (ADR-0159 §3).
 *
 * Discovery + a merchant-supplied endpoint illustration; unchanged in
 * spirit from the original monolithic view. A signed handshake against
 * a self-built agent is the ideal shape when the merchant controls both
 * sides of the pipe.
 */

import { buildDiscoveryPhase, buildReadMenuPhase } from './discovery.js';
import type { JourneyContext, Phase } from './types.js';

export function buildCustomJourney(ctx: JourneyContext): Phase[] {
  const { agent, live, installed, cardUrl, catalogUrl, liveProducts, cart } = ctx;
  return [
    buildDiscoveryPhase(agent, installed, cardUrl, live.card),
    buildReadMenuPhase(agent, installed, catalogUrl, live.catalog, liveProducts),
    {
      id: 'identity',
      name: 'Identity check',
      tier: 'illustration',
      narration:
        `${agent.name} presents a signed identity from ${agent.org}. With a registered ed25519 key, Sill would verify the signature and let it through — that's the handshake a self-built agent can implement.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '{ "verified": "illustration — signed handshake not performed here", "agent": "custom" }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'intent',
      name: 'Intent mandate',
      tier: 'illustration',
      narration: `${agent.name}'s operator confirmed "buy 2× ${cart.title}${cart.is_sample ? ' [sample]' : ''}." It issues a signed intent mandate.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '{ "mandate": "mnd_…", "decision": "approved" }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'cart',
      name: 'Cart',
      tier: 'illustration',
      narration: cart.unit_price_display.length > 0
        ? `It builds the cart: 2 × ${cart.title}${cart.is_sample ? ' [sample]' : ''} @ ${cart.unit_price_display} = ${cart.line_total_display}, + tax & shipping ≈ ${cart.total_display}.${cart.is_sample ? ' (Placeholder — your store has no products synced yet.)' : ''}`
        : `It builds the cart: 2 × ${cart.title}${cart.is_sample ? ' [sample]' : ''}, + tax & shipping illustrated below.${cart.is_sample ? ' (Placeholder — your store has no products synced yet.)' : ''}`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: cart.unit_minor !== null
          ? `{ "mandate": "mnd_…", "total_minor": ${Math.round(cart.unit_minor * 2 * 1.15)} }`
          : '{ "mandate": "mnd_…", "total_minor": null }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'pay',
      name: 'Payment',
      tier: 'illustration',
      narration: `It submits a signed payment mandate. Sill checks it against your rules, then settles via Stripe and writes a signed audit record. (Illustration — no real charge here.)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: cart.unit_minor !== null
          ? `{ "decision": "approved", "settled": true, "amount_minor": ${Math.round(cart.unit_minor * 2 * 1.15)} }`
          : '{ "decision": "approved", "settled": true }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'fulfill',
      name: 'Fulfillment',
      tier: 'illustration',
      narration: `Order placed, tracking issued, customer emailed — every step would land in your signed audit log.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '{ "fulfilled": true, "tracking": "1Z999AA1…" }',
        live: false,
      },
      ok: true,
    },
  ];
}
